// Printed proposal footer, repeated on every sheet:
//
//   ───────────────────── 1px green hairline ─────────────────────
//                           Decks Unique                Page 1 of 3
//       123 Example Ave, Commack NY · (631) 555-1234 · decksunique.com
//
// It lives in the @page bottom margin boxes, NOT in the document, so it takes
// no room from the page body and the page breaks are exactly what they were
// without it. ProposalPage injects footerPageCss() into its print-time @page
// style (appended last, so it beats ST124Form.css's global rule that blanks
// every margin box).
//
// A margin box has one font, size and colour for all its text, so the two
// centred lines are an SVG image (each line styled on its own) carrying
// Instrument Serif inline — an SVG image can't fetch fonts. Chrome only has
// that font ready at print time if the same image was already rendered on the
// page, so ProposalPage also keeps an off-screen <img> of it (see
// .du-printFooterPreload). The text stays real, selectable text in the PDF.
//
// House rule: Instrument Serif is never uppercased or letterspaced.

import { INSTRUMENT_SERIF_WOFF2_BASE64 } from "./proposalFooterFont";

export type FooterSettings = {
  companyName?: string | null;
  companyAddress?: string | null;
  companyPhone?: string | null;
  companyWebsite?: string | null;
};

export type FooterText = { name: string; line: string };

const GREEN = "#1f6b3e"; // --du-green
const NAME_INK = "#3b3b36"; // --du-body
// --du-muted, not the lighter --du-footer: small warm grey prints much fainter
// than it looks on screen (see the --du-label note in ProposalCD.css).
const MUTED = "#6b6b63";

// SVG geometry, in CSS px. The bottom @page margin is 0.5in = 48px; the image
// sits 4px under the hairline, so the address line's descenders end ~18px
// (≈0.19in) above the paper edge — clear of a typical printer's unprintable
// strip.
export const FOOTER_SVG_WIDTH = 520;
export const FOOTER_SVG_HEIGHT = 30;
const NAME_SIZE = 12;
const NAME_BASELINE = 11;
const LINE_SIZE = 9.5;
const LINE_BASELINE = 24;
// Rough character budgets at full size (Instrument Serif is narrow, ~0.42em
// per character). A longer line is set smaller rather than clipped.
const NAME_MAX_CHARS = 90;
const LINE_MAX_CHARS = 120;

const clean = (v: unknown) =>
  typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "";

/** Name and "address · phone · website" from the (org) settings. */
export function footerText(
  s: FooterSettings | null | undefined,
  formatPhone: (raw: string) => string = (raw) => raw
): FooterText {
  const phone = clean(s?.companyPhone);
  return {
    name: clean(s?.companyName),
    line: [
      clean(s?.companyAddress),
      phone ? formatPhone(phone) || phone : "",
      clean(s?.companyWebsite),
    ]
      .filter(Boolean)
      .join(" · "),
  };
}

const xml = (v: string) =>
  v
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const fit = (text: string, size: number, maxChars: number) =>
  text.length > maxChars ? +((size * maxChars) / text.length).toFixed(2) : size;

/** The two centred lines as an SVG document, or "" when there's nothing to print. */
export function footerSvg({ name, line }: FooterText): string {
  if (!name && !line) return "";
  const x = FOOTER_SVG_WIDTH / 2;
  // With only one line, it takes the name's (top) position.
  const rows: string[] = [];
  if (name) {
    rows.push(
      `<text x="${x}" y="${NAME_BASELINE}" font-size="${fit(name, NAME_SIZE, NAME_MAX_CHARS)}" fill="${NAME_INK}">${xml(name)}</text>`
    );
  }
  if (line) {
    rows.push(
      `<text x="${x}" y="${name ? LINE_BASELINE : NAME_BASELINE}" font-size="${fit(line, LINE_SIZE, LINE_MAX_CHARS)}" fill="${MUTED}">${xml(line)}</text>`
    );
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${FOOTER_SVG_WIDTH}" height="${FOOTER_SVG_HEIGHT}" viewBox="0 0 ${FOOTER_SVG_WIDTH} ${FOOTER_SVG_HEIGHT}">` +
    `<style>@font-face{font-family:"DU Footer Serif";font-weight:400;src:url(data:font/woff2;base64,${INSTRUMENT_SERIF_WOFF2_BASE64}) format("woff2")}` +
    `text{font-family:"DU Footer Serif",serif;font-weight:400;text-anchor:middle}</style>` +
    rows.join("") +
    `</svg>`
  );
}

/** data: URI for footerSvg, or "" when there's nothing to print. */
export function footerSvgUri(text: FooterText): string {
  const svg = footerSvg(text);
  if (!svg) return "";
  // btoa needs Latin-1; encode UTF-8 first so "·" and accented names survive.
  const bin = encodeURIComponent(svg).replace(/%([0-9A-F]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
  return `data:image/svg+xml;base64,${btoa(bin)}`;
}

/** @page margin-box rules for the footer, or "" (no footer) without company info. */
export function footerPageCss(uri: string): string {
  if (!uri) return "";
  const rule = `border-top: 1px solid ${GREEN}; vertical-align: top;`;
  return `
  @bottom-left { content: ""; ${rule} }
  @bottom-center { content: url("${uri}"); ${rule} padding-top: 4px; }
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    ${rule}
    padding-top: 6px;
    text-align: right;
    font-family: "Instrument Serif", serif;
    font-weight: 400;
    font-size: 9px;
    color: ${MUTED};
  }`;
}
